import React, { useMemo } from "react";
import { formatUSD } from "@/utils/formatters";
import { useCurrencyMode } from "@/context/CurrencyContext";
import { formatDateKeyDisplay } from "../../lib/timezone";
import type { LeaderboardEntry } from "../../types";
import CohortRecoveryCard from "./CohortRecoveryCard";
import HoverTooltip from "./HoverTooltip";
import { MIN_VOLUME_GATE, type SectorFilter } from "./constants";
import {
  buildComponentRows,
  computeQualifiedMaxes,
  computeRepJourney,
  computeFederer,
} from "./scoreBreakdownUtils";

// =============================================================================
// REP BREAKDOWN PANEL — inline "why is this rep ranked here" expansion
// =============================================================================
// Renders under a leaderboard row when it's clicked. Same visual language as
// the exec CalculationsDrawer's SCORE BREAKDOWN section (norm×weight rows,
// Federer callout, cohort recovery, daily journey) but scoped to ONE rep and
// visible to every viewer — the numbers shown are the same ones already on
// the row.
//
// POINTS vs SCORE are two DIFFERENT numbers (S33 decision 4): POINTS is the
// prestige total (lb ± kpi + mvp) that ranks the range-mode table; SCORE is
// the rank_score quality composite used as tiebreak. Both are labeled.
// =============================================================================

interface RepBreakdownPanelProps {
  entry: LeaderboardEntry;
  sector: SectorFilter;
  cohortEntries: LeaderboardEntry[]; // ranked sector cohort — sets the norm denominators
  dailyRows: LeaderboardEntry[]; // raw daily rows — journey strip + Federer stats
  timezone: string;
  cohortOrgId: string | null; // null → skip CohortRecoveryCard (company view)
  cohortPeriodStart: string | null;
}

const RepBreakdownPanel: React.FC<RepBreakdownPanelProps> = ({
  entry,
  sector,
  cohortEntries,
  dailyRows,
  timezone,
  cohortOrgId,
  cohortPeriodStart,
}) => {
  const gate = MIN_VOLUME_GATE[sector];
  const isUnranked = !entry.rank;
  // Dep of the `components` useMemo below (buildComponentRows pre-formats Cash/CPC
  // with formatUSD) so it recomputes on a USD⇄AUD flip; also subscribes this
  // component so it re-renders on flip (it has no other currency trigger).
  const currencyMode = useCurrencyMode();

  const maxes = useMemo(
    () => computeQualifiedMaxes(cohortEntries, sector),
    [cohortEntries, sector]
  );
  const components = useMemo(
    () => buildComponentRows(entry, sector, maxes),
    [entry, sector, maxes, currencyMode]
  );
  const journey = useMemo(
    () => computeRepJourney(dailyRows, entry.rep_id, sector),
    [dailyRows, entry.rep_id, sector]
  );
  const federer = useMemo(
    () => computeFederer(dailyRows, entry.rep_id, sector),
    [dailyRows, entry.rep_id, sector]
  );

  // Decision 6 locked format, ASCII signs: "{lb} lb {+|-} {|kpi|} kpi + {mvp} mvp = {total} pts"
  const pointsLine =
    entry.prestige_total != null
      ? `${entry.points_earned || 0} lb ${(entry.kpi_points ?? 0) < 0 ? "-" : "+"} ${Math.abs(entry.kpi_points ?? 0)} kpi + ${entry.mvp_points ?? 0} mvp = ${entry.prestige_total} pts`
      : `${entry.points_earned || 0} pts this period`;

  return (
    <div className="px-6 py-4 bg-black/[0.02] border-t border-black/[0.04] animate-fade-in">
      {/* POINTS vs SCORE — two distinct numbers, both labeled */}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-2 mb-3">
        <div>
          <p className="token-10 uppercase text-glass-muted">Points — prestige total</p>
          <p className="token-13m text-glass mt-0.5">{pointsLine}</p>
        </div>
        <div>
          <p className="token-10 uppercase text-glass-muted">
            Score — quality composite (tiebreak)
          </p>
          <p className="token-13m text-glass mt-0.5">
            {entry.rank_score != null ? entry.rank_score.toFixed(3) : "—"}
          </p>
        </div>
        {isUnranked && (
          <div>
            <p className="token-10 uppercase text-glass-muted">Rank</p>
            <p className="token-13 text-ds-amber mt-0.5">Unranked — needs {gate.label}</p>
          </div>
        )}
      </div>

      {/* Federer-effect callout — leadership days + tightest gap */}
      {federer.totalDays > 0 && (
        <div className="mb-3 p-2 rounded-md bg-black/[0.02] border border-black/[0.04]">
          <p className="token-13 text-glass-secondary">
            Led on <span className="text-ds-accent font-semibold">{federer.leadDays}</span> of{" "}
            {federer.totalDays} days
            {federer.tightestGap && (
              <>
                {" — tightest gap "}
                <span className="text-glass">
                  {formatDateKeyDisplay(federer.tightestGap.date, timezone)}
                </span>{" "}
                ({(federer.tightestGap.myScore * 100).toFixed(0)}% vs {federer.tightestGap.nextRep}{" "}
                {(federer.tightestGap.nextScore * 100).toFixed(0)}
                %)
              </>
            )}
          </p>
        </div>
      )}

      {/* Norm × weight component rows */}
      <div className="grid gap-2">
        {components.map((c) => (
          <HoverTooltip
            key={c.label}
            label={c.label}
            formula={c.formula}
            computation={c.computation}
          >
            <div className="flex items-center gap-3 py-1.5 border-b border-black/[0.04] last:border-b-0 cursor-help hover:bg-black/[0.02] rounded">
              <span className="token-10 text-glass-muted w-16 uppercase">{c.label}</span>
              <span className="token-13m text-glass w-20 text-right">{c.raw}</span>
              <span className="token-11 text-glass-muted w-16 text-right">
                norm: {c.norm.toFixed(2)}
              </span>
              <span className="token-11 text-glass-muted w-12 text-center">
                x {(c.weight * 100).toFixed(0)}%
              </span>
              <span className="token-11 text-glass-muted">=</span>
              <span className="token-13m text-ds-accent w-12 text-right">
                {c.contribution.toFixed(2)}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
                <div
                  className="h-full rounded-full bg-ds-accent/60"
                  style={{ width: `${Math.min(Math.max(Math.round(c.norm * 100), 0), 100)}%` }}
                />
              </div>
            </div>
          </HoverTooltip>
        ))}
      </div>

      {/* Cohort recovery sidecar — late resolutions for this rep + period */}
      {cohortOrgId && cohortPeriodStart && (
        <CohortRecoveryCard
          orgId={cohortOrgId}
          repId={entry.rep_id}
          periodStart={cohortPeriodStart}
        />
      )}

      {/* Day-by-day journey strip — per-day rank + raw on hover */}
      {journey.length > 0 && (
        <div className="mt-3 pt-3 border-t border-black/[0.04]">
          <p className="token-10 text-glass-muted uppercase mb-2">
            Daily journey ({journey.length} {journey.length === 1 ? "day" : "days"})
          </p>
          <div className="flex flex-wrap gap-1">
            {journey.map((d) => {
              const rankColor =
                d.rank === 1
                  ? "bg-ds-amber/20 text-ds-amber border-ds-amber/30"
                  : d.rank === 2
                    ? "bg-black/[0.05] text-glass-secondary border-black/[0.08]"
                    : d.rank === 3
                      ? "bg-black/[0.03] text-glass-muted border-black/[0.06]"
                      : "bg-black/[0.02] text-glass-muted border-black/[0.04]";
              const tooltipFormula = `Rank ${d.rank ?? "—"} · ${d.rank_points} pts`;
              const tooltipComputation = `Cash ${formatUSD(d.cash)} · Shows ${d.shows}/${d.shows + d.no_shows + d.orphan_precalls} (${(d.show_rate * 100).toFixed(0)}%) · CPC ${formatUSD(d.cpc)}`;
              return (
                <HoverTooltip
                  key={d.date}
                  label={formatDateKeyDisplay(d.date, timezone)}
                  formula={tooltipFormula}
                  computation={tooltipComputation}
                >
                  <div
                    className={`flex flex-col items-center justify-center w-12 h-12 rounded border cursor-help ${rankColor}`}
                  >
                    <span className="token-11 leading-none">{d.date.slice(5)}</span>
                    <span className="token-13 font-bold leading-tight mt-0.5">
                      #{d.rank ?? "—"}
                    </span>
                    <span className="token-11 leading-none opacity-70">{d.rank_points}p</span>
                  </div>
                </HoverTooltip>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RepBreakdownPanel;
